import { FC } from "react";

import { useSnackbar, VariantType } from "notistack";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import DefaultClientsForm, { ClientFormData } from "./DefaultClientForm";
import { createClient } from "@/api/clients";
import {
  FormTitle,
  FormWrapper,
} from "@/components/Forms/components/MonolithFormComponents";
import {
  processCustomAttributesForCreation,
  extractCustomFieldsFromFormData,
} from "./utils/customAttributeUtils";

interface CreateClientFormProps {
  width?: number | undefined;
  onSubmit: () => void; // Refetch query from parent component
  onCancel: () => void;
  importButton?: React.ReactNode;
}

const CreateClientForm: FC<CreateClientFormProps> = ({
  width,
  onSubmit,
  onCancel,
  importButton,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();

  const handleSubmit = async (data: ClientFormData) => {
    try {
      // Process custom fields for API submission
      const customAttributes = processCustomAttributesForCreation(data);
      const cleanedFormData = extractCustomFieldsFromFormData(data);

      // Prepare payload with cleaned data and custom attributes
      const payload = {
        ...cleanedFormData,
        ...(customAttributes.length > 0 && {
          custom_attributes: customAttributes,
        }),
      };

      await createClient(payload);
      onSubmit?.();
      enqueueSnackbar(`Client ${data?.name || ""} created`, {
        variant: "success" as VariantType,
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Client: ${data?.name}`,
        event: "client:create",
        object_type: "client",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this item. Please try again.",
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <FormWrapper width={width}>
      <FormTitle style={{ display: "flex", alignItems: "center" }}>
        Create Client{" "}
        {importButton && (
          <div style={{ display: "inline-block", marginLeft: "auto" }}>
            {importButton}
          </div>
        )}
      </FormTitle>
      <DefaultClientsForm
        onSubmit={handleSubmit}
        onCancel={onCancel}
        buttonProps={{ submitText: "Create Client" }}
      />
    </FormWrapper>
  );
};

export default CreateClientForm;
